import React, { useEffect, useState } from 'react';
import { adminApi, organizationsApi } from '../services/api';
import { User, Organization } from '../types';
import toast from 'react-hot-toast';
import './UserManager.css';
import { useAuth } from '../contexts/AuthContext';

function UserManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState<Organization[]>([]);

  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    organization_id: 0,
    role: 'user',
  });
  const [creating, setCreating] = useState(false);

  // Edit modal state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ full_name: '', role: 'user', is_active: true, organization_id: 0 });
  const [savingEdit, setSavingEdit] = useState(false);

  const canChangeOrganization = !!(currentUser && (currentUser.role === 'superadmin' || currentUser.is_superuser));

  useEffect(() => {
    loadUsers();
    loadOrgs();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listUsers();
      setUsers(data);
    } catch (e: any) {
      console.error(e);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgs = async () => {
    try {
      const data = await organizationsApi.list();
      setOrgs(data);
    } catch (e) {
      // non-fatal
      console.warn('Failed to load organizations', e);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete user?')) return;

    // Prompt for password confirmation (step-up authentication)
    const password = window.prompt('Please enter your password to confirm user deletion:');
    if (!password) {
      toast.error('Password required to delete user');
      return;
    }

    try {
      await adminApi.deleteUser(id, password);
      toast.success('User deleted');
      loadUsers();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'organization_id' ? Number(value) : value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.organization_id) {
      toast.error('Please fill email, password and organization');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      await adminApi.createUser({
        email: form.email,
        password: form.password,
        full_name: form.full_name || undefined,
        organization_id: form.organization_id,
        role: form.role,
      });
      toast.success('User created');
      setForm({ email: '', password: '', full_name: '', organization_id: 0, role: 'user' });
      loadUsers();
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Failed to create user';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const getOrgName = (orgId: number) => {
    const o = orgs.find(x => x.id === orgId);
    return o ? o.name : String(orgId);
  };

  // Edit handlers
  const openEdit = (user: User) => {
    setEditingUser(user);
    setEditForm({ full_name: user.full_name || '', role: user.role as string, is_active: user.is_active, organization_id: user.organization_id });
  };

  const handleSetPassword = async (userId: number, email: string) => {
    const isSuperAdmin = currentUser && (currentUser.role === 'superadmin' || currentUser.is_superuser);
    if (!isSuperAdmin) {
      toast.error('Only super admins can change passwords');
      return;
    }
    
    const newPassword = window.prompt(`Enter new password for ${email}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    const confirm = window.prompt('Confirm new password:');
    if (newPassword !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await adminApi.setPassword(userId, newPassword);
      toast.success('Password updated successfully');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
  };

  const closeEdit = () => {
    setEditingUser(null);
    setEditForm({ full_name: '', role: 'user', is_active: true, organization_id: 0 });
    setSavingEdit(false);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'is_active') {
      setEditForm(prev => ({ ...prev, is_active: (e.target as HTMLInputElement).checked }));
    } else if (name === 'organization_id') {
      setEditForm(prev => ({ ...prev, organization_id: Number(value) }));
    } else {
      setEditForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const submitEdit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editingUser) return;
    setSavingEdit(true);
    try {
      await adminApi.updateUser(editingUser.id, {
        full_name: editForm.full_name,
        role: editForm.role,
        is_active: editForm.is_active,
        organization_id: canChangeOrganization ? editForm.organization_id : undefined,
      });
      toast.success('User updated');
      loadUsers();
      closeEdit();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to update user');
      setSavingEdit(false);
    }
  };

  if (loading) return <div className="loading">Loading users...</div>;

  return (
    <div className="user-manager">
      <h1>Users</h1>

      <div className="card create-card">
        <h2>Create new user</h2>
        <form onSubmit={handleCreate} className="create-form">
          <div className="form-row">
            <label>Email</label>
            <input name="email" type="email" value={form.email} onChange={handleChange} />
          </div>

          <div className="form-row">
            <label>Password</label>
            <input name="password" type="password" value={form.password} onChange={handleChange} />
          </div>

          <div className="form-row">
            <label>Full name</label>
            <input name="full_name" type="text" value={form.full_name} onChange={handleChange} />
          </div>

          <div className="form-row">
            <label>Organization</label>
            <select name="organization_id" value={form.organization_id} onChange={handleChange}>
              <option value={0}>Select organization...</option>
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label>Role</label>
            <select name="role" value={form.role} onChange={handleChange}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>

          <div className="form-row">
            <button className="btn btn-primary" type="submit" disabled={creating}>
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>

      <div className="users-list card">
        <h2>Existing users ({users.length})</h2>
        {users.length === 0 ? (
          <p>No users found</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Full Name</th>
                <th>Role</th>
                <th>Active</th>
                <th>Organization</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.full_name}</td>
                  <td>{u.role}</td>
                  <td>{u.is_active ? 'Yes' : 'No'}</td>
                  <td>{getOrgName(u.organization_id)}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(u)}>Edit</button>
                    {canChangeOrganization && (
                      <button className="btn btn-outline btn-sm" onClick={() => handleSetPassword(u.id, u.email)} style={{ marginLeft: 8 }}>Password</button>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => handleDelete(u.id)} style={{ marginLeft: 8 }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Edit user: {editingUser.email}</h3>
            <form onSubmit={submitEdit} className="edit-form">
              <div className="form-row">
                <label>Full name</label>
                <input name="full_name" value={editForm.full_name} onChange={handleEditChange} />
              </div>

              <div className="form-row">
                <label>Role</label>
                <select name="role" value={editForm.role} onChange={handleEditChange}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>

              <div className="form-row">
                <label>Organization</label>
                <select
                  name="organization_id"
                  value={editForm.organization_id}
                  onChange={handleEditChange}
                  disabled={!canChangeOrganization}
                >
                  <option value={0}>Select organization...</option>
                  {orgs.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                {!canChangeOrganization && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Only super admins can change organization
                  </div>
                )}
              </div>

              <div className="form-row">
                <label>
                  <input type="checkbox" name="is_active" checked={!!editForm.is_active} onChange={handleEditChange} /> Active
                </label>
              </div>

              <div className="form-row modal-actions">
                <button className="btn btn-primary" type="submit" disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</button>
                <button type="button" className="btn btn-outline" onClick={closeEdit} style={{ marginLeft: 8 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManager;
